import { RefObject, useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import { EChartsOption } from "echarts";

import { Theme, useTheme } from "Shared/Hook/useTheme";
import { themeColors } from "Styles/tokens";

export type ChartTheme = {
  text: string;
  muted: string;
  success: string;
  warning: string;
};

// Canvas can't read CSS variables, so resolve the `--dt-*` tokens to colors.
const readChartTheme = (theme: Theme): ChartTheme => {
  const styles = getComputedStyle(document.documentElement);
  const token = (name: string, fallback: string) =>
    styles.getPropertyValue(name).trim() || fallback;
  const fallback = themeColors[theme];
  return {
    text: token("--dt-text", fallback.text),
    muted: token("--dt-muted", fallback.muted),
    success: token("--dt-success", fallback.success),
    warning: token("--dt-warning", fallback.warning),
  };
};

/** Token colors for charts, re-read whenever the app switches theme. */
export const useChartTheme = (): ChartTheme => {
  const theme = useTheme();
  return useMemo(() => readChartTheme(theme), [theme]);
};

/**
 * Owns one ECharts instance on `ref`: created once, updated with setOption
 * when `option` changes, resized with its container and disposed on unmount.
 * Memoise `option` (with the theme from useChartTheme in its deps) so parent
 * re-renders don't reach the chart and theme switches do.
 */
export const useEChart = (
  ref: RefObject<HTMLElement>,
  option: EChartsOption,
) => {
  const chart = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    const instance = echarts.init(container);
    chart.current = instance;

    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        // Skip while hidden (e.g. a collapsed section) to keep the last size.
        if (container.clientWidth > 0) instance.resize();
      });
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      instance.dispose();
      chart.current = null;
    };
  }, [ref]);

  useEffect(() => {
    // Replace rather than merge, so a title, axis or series the new option
    // leaves out is removed instead of lingering from the last data.
    chart.current?.setOption(option, { notMerge: true });
  }, [option]);
};
